/**
 * Quote-card layout for Nationwide CareMatters Annuity, single (2943) and joint
 * (2944).
 *
 * Labels follow the "Your contract highlights" page of the carrier illustration.
 * Four things about this product are not obvious from the quote row:
 *
 * - The monthly benefit is one figure for the whole contract, on a joint quote
 *   too. The illustration prints it once, and it equals the total benefit
 *   divided by the benefit period. It must not be doubled per annuitant.
 * - The benefit period is printed in months for single and joint alike.
 * - Guaranteed Interest Rate (3%) and the Contingent Deferred Sales Charge
 *   period (10 Years) are not stored on the quote row. They are the same on
 *   every illustration we hold (23 samples), so they are fixed here. If the
 *   carrier changes either, change it in this file.
 * - The risk class is stored in primaryTobaccoStatus, inverted: Standard is
 *   saved as Non-Tobacco and Preferred as Tobacco (see
 *   transformCareMattersAnnuityCSV.ts). The product does not underwrite on
 *   tobacco. params.riskClass holds the real label when the backfill has run,
 *   and wins. Both annuitants always share one class.
 *
 * The two rider premiums are part of the Total Premium Amount, not on top of
 * it. The illustration prints "Not Elected" when a rider is off, and so do we.
 * The row stores them at the scraped $100,000 premium; adjustQuoteForPremium
 * re-prices them when the premium changes, so this file prints what it is given.
 */

import {
  benefitPeriod,
  currency,
  electedPremium,
  fixed,
  monthlyBenefit,
  premium,
} from './fieldBank';
import type { QuoteCardField, QuoteCardLayout } from './types';

const RISK_CLASS_BY_STORED_TOBACCO_STATUS: Record<string, string> = {
  'Non-Tobacco': 'Standard',
  'Tobacco': 'Preferred',
};

const riskClass =
  (label: string): QuoteCardField =>
  quote => {
    const riskClassLabel =
      quote.params?.riskClass ??
      RISK_CLASS_BY_STORED_TOBACCO_STATUS[quote.primaryTobaccoStatus] ??
      quote.primaryTobaccoStatus;
    return {
      label,
      value: quote.secondaryTobaccoStatus ? `${riskClassLabel} (both annuitants)` : riskClassLabel,
    };
  };

export const careMattersAnnuityLayout: QuoteCardLayout = [
  {
    title: 'Initial LTC Benefit Limit',
    fields: [
      monthlyBenefit('Maximum Monthly LTC Benefit Day 1'),
      currency('Total LTC Benefit Day 1', quote => quote.ltcBenefitTotal),
      benefitPeriod('LTC Benefit Period', 'months'),
    ],
  },
  {
    title: 'Features and Benefits',
    fields: [
      fixed('Guaranteed Interest Rate', '3%'),
      fixed('Contingent Deferred Sales Charge', '10 Years'),
      riskClass('Class'),
    ],
  },
  {
    title: 'Premium',
    fields: [
      premium('Total Premium Amount'),
      electedPremium(
        'Inflation Protection Premium',
        quote => quote.params?.inflationProtectionPremium,
      ),
      electedPremium(
        'Nonforfeiture Benefit Premium',
        quote => quote.extensionOfBenefitsNonforfeitureRiderAmount,
      ),
    ],
  },
];
